import { Component } from "react"

/* Redux */
import { connect } from "react-redux"

/* Components */
import { InnerLayout, Pagination, Avatar } from "../../components"

/* Widgets */
import { BookingForm, BookingFilter, BookingWindow } from "../../widgets/Booking"

/* REST API */
import { list, cancel } from "../../api/Booking"

/* Helpers */
import { withRouter, utils } from "../../helpers"

/* Icons */
import { SearchIcon, CloseIcon, FilterIcon, PlusIcon, CalendarIcon, ClockIcon, VipIcon, TableIcon, StudioIcon, OperatorIcon, AdminIcon, CancelIcon, CommentIcon } from "../../widgets/Booking/BookingIcons"

/* Utils */
import { getState, getStateText, getGame, getPlayerName, getAvatar, formatCount, formatWhen, formatDate, formatRange, formatDuration, formatCountdown } from "../../widgets/Booking/BookingUtils"

/* Translate */
import { t } from "../../translates/translate"

/* Sweet alert */
import Swal from "../../helpers/alert"

/* Moment */
import moment from "moment"


/* PAGE SIZE — THE SAME NUMBER THE CONTROLLER USES */
const SIZE = 20

/* DELAY BEFORE THE SEARCH REQUEST LEAVES */
const DELAY = 400

/* HOW OFTEN THE LIST REDRAWS ITSELF — A COUNTDOWN THAT NEVER MOVES IS WORSE THAN NO COUNTDOWN */
const TICK = 30000

/* EMPTY ANSWER — THE SAME SHAPE THE CONTROLLER SENDS */
const EMPTY = {
    counts: { upcoming: 0, history: 0 },
}


/* Page Bookings */
class Bookings extends Component {

    constructor() {
        super()

        this.state = {
            data: [],
            status: "loading",
            count: 0,
            tab: "upcoming",
            search: "",
            operators: [],
            tables: [],
            selected: "all",
            start: null,
            end: null,
            bookingStatus: "",
            counts: EMPTY.counts,
            now: Date.now(),
        }
    }

    componentDidMount = () => {
        window.scrollTo(0, 0)
        this.load()

        /* The countdown of every row is read from the clock, so the page keeps its own heartbeat */
        this._tick = setInterval(() => this.setState({ now: Date.now() }), TICK)
    }

    componentDidUpdate = (prevProps) => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page — a new page starts from the top with the loader */
        if (prevProps.params.page !== page) {
            window.scrollTo(0, 0)
            this.load(true)
        }

    }

    componentWillUnmount = () => {
        clearTimeout(this._timer)
        clearInterval(this._tick)
    }

    /* Get Page */
    getPage = () => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page */
        if (Number.isInteger(parseInt(page))) {
            return parseInt(page)
        }

        return 1
    }

    /* Load Action */
    load = (reload = false) => {

        /* Fields */
        const { tab, search, operators, tables, start, end, bookingStatus } = this.state
        const page = this.getPage()

        /* Check Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Send to REST API */
        list({
            page,
            type: tab,
            search,
            operators: operators.map((item) => item.id).join(","),
            tables: tables.map((item) => item.id).join(","),
            status: bookingStatus,
            start: start ? moment(start).format("YYYY-MM-DD HH:mm:ss") : "",
            end: end ? moment(end).format("YYYY-MM-DD HH:mm:ss") : "",
        }).then((response) => {
            if (response.status === 200) {
                this.setState({
                    status: "ok",
                    data: response.data.list,
                    count: response.data.count,
                    counts: response.data.counts ? response.data.counts : EMPTY.counts,
                    now: Date.now(),
                })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", data: [], count: 0, ...EMPTY })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", data: [], count: 0, ...EMPTY })
            }
            else {
                this.setState({ status: "error", data: [], count: 0, ...EMPTY })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], count: 0, ...EMPTY })
        })

    }

    /* Change Tab — the upcoming reservations and the history are two lists, so the page starts over */
    changeTab = (tab) => {

        /* Check Tab */
        if (this.state.tab === tab) {
            return
        }

        this.setState({ tab, status: "loading" }, () => this.apply())
    }

    /* Change Search — the request leaves only after the typing stops */
    changeSearch = (search) => {

        this.setState({ search })

        clearTimeout(this._timer)
        this._timer = setTimeout(() => this.apply(), DELAY)
    }

    /* Apply Filter */
    applyFilter = ({ selected, start, end, operators, tables, status }) => {
        this.setState({ selected, start, end, operators, tables, bookingStatus: status }, () => this.apply())
    }

    /* Apply Action — every filter and every tab restart from the first page */
    apply = () => {

        /* Fields */
        const { navigate } = this.props

        /* Check Page */
        if (this.getPage() !== 1) {
            navigate("/booking/1")
            return
        }

        this.load()
    }

    /* Read The Error Message Of A Response */
    message = (response) => {

        if (response && response.data && response.data.message) {
            return response.data.message
        }

        if (response && response.status === 498) {
            return t("swal.invalidKey")
        }

        if (response && (response.status === 401 || response.status === 403)) {
            return t("swal.noAccess")
        }

        return t("swal.generic")
    }

    /* Cancel Action — the row stays in the history, only the state changes
     *
     * A reservation is called off at any time, so the question has to say what exactly is being called off:
     * a table that is already held or a player who has already come into the game */
    cancelBooking = (item) => {

        /* Fields */
        const state = getState(item)

        /* Note — what the shift is about to break */
        const note = state === "arrived"
            ? ` · ${t("book.cancelArrivedNote")}`
            : state === "live" ? ` · ${t("book.cancelLiveNote")}` : ""

        Swal.fire({
            title: t("book.cancelConfirm"),
            text: `${getGame(item)} · ${formatWhen(item.startAt)} ${formatRange(item)} · ${item.player}${note}`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f44336",
            reverseButtons: true,
            confirmButtonText: t("book.cancelButton"),
            cancelButtonText: t("swal.cancelButton"),
        }).then((result) => {

            /* Check Answer */
            if (!result.isConfirmed) {
                return
            }

            /* Send to REST API */
            cancel({ id: item.id }).then((response) => {
                if (response.status === 200) {
                    Swal.fire(utils.notification(t("book.cancelledSuccess"), "success"))
                    this.load(true)
                }
                else {
                    Swal.fire(utils.notification(this.message(response), "error"))
                }
            }).catch(() => {
                Swal.fire(utils.notification(t("swal.generic"), "error"))
            })

        })
    }

    /* Count Of The Active Filters — the badge of the filter button */
    getCount = () => {

        /* Fields */
        const { selected, operators, tables, bookingStatus } = this.state
        let count = 0

        if (selected !== "all") {
            count = count + 1
        }

        if (operators.length > 0) {
            count = count + 1
        }

        if (tables.length > 0) {
            count = count + 1
        }

        if (bookingStatus !== "") {
            count = count + 1
        }

        /* Send Result */
        return count
    }

    /* Draw Search */
    _search = () => {

        /* Fields */
        const { search } = this.state

        return (
            <div className="bkg--search">

                <SearchIcon />

                <input
                    value={search}
                    onChange={(event) => this.changeSearch(event.target.value)}
                    placeholder={t("book.search")}
                />

                {search !== "" &&
                    <div onClick={() => this.changeSearch("")} className="bkg--search-clear">
                        <CloseIcon size={12} />
                    </div>
                }

            </div>
        )
    }

    /* Draw Filter Button */
    _filter = () => {

        /* Fields */
        const { selected, start, end, operators, tables, bookingStatus } = this.state
        const count = this.getCount()

        return (
            <div
                onClick={() => this._filterModal.open({ selected, start, end, operators, tables, status: bookingStatus })}
                className={`bkg--filter ${count > 0 ? "active" : ""}`}
            >
                <FilterIcon size={15} />
                <span>{t("book.filter")}</span>
                {count > 0 && <div className="bkg--filter-count">{count}</div>}
            </div>
        )
    }

    /* Draw Create Button — a viewer reads the section but never writes into it */
    _create = () => {

        /* Fields */
        const { admin } = this.props

        /* Check Access */
        if (!utils.can(admin, "edit")) {
            return null
        }

        return (
            <div onClick={() => this._formModal.open()} className="bkg--create">
                <PlusIcon size={15} />
                <span>{t("book.create")}</span>
            </div>
        )
    }

    /* Draw Tabs — "предстоящие" holds everything that is still ahead or running, the rest is history */
    _tabs = () => {

        /* Fields */
        const { tab, counts } = this.state

        return (
            <div className="bkg--tabs">

                <div onClick={() => this.changeTab("upcoming")} className={`bkg--tab ${tab === "upcoming" ? "active" : ""}`}>
                    <CalendarIcon size={15} />
                    <span>{t("book.tab.upcoming")}</span>
                    <div className="bkg--tab-count">{formatCount(counts.upcoming)}</div>
                </div>

                <div onClick={() => this.changeTab("history")} className={`bkg--tab ${tab === "history" ? "active" : ""}`}>
                    <ClockIcon size={15} />
                    <span>{t("book.tab.history")}</span>
                    <div className="bkg--tab-count">{formatCount(counts.history)}</div>
                </div>

            </div>
        )
    }

    /* Draw Actions — moving the window and calling a reservation off, both open to a superadmin,
     * a manager and a pitboss
     *
     * The arrival is not marked by hand: it is written by the start of the session. Everything else is
     * open at any time — the guest is late, the game runs longer or the table has to be free earlier,
     * and a reservation is called off even after the player has come in. Only a cancelled row is read only */
    _actions = (item) => {

        /* Fields */
        const { admin } = this.props
        const state = getState(item)

        /* Check Access */
        if (!utils.can(admin, "edit")) {
            return null
        }

        /* Check State — a cancelled reservation holds nothing anymore, it is written anew instead */
        if (state === "cancelled") {
            return <div className="bkg--action-empty">—</div>
        }

        return (
            <>

                <div onClick={() => this._windowModal.open(item)} className="bkg--action blue" title={t("book.windowTitleAction")}>
                    <ClockIcon size={15} />
                </div>

                <div onClick={() => this.cancelBooking(item)} className="bkg--action red" title={t("book.cancelTitle")}>
                    <CancelIcon size={15} />
                </div>

            </>
        )
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status, data, count, search, tab } = this.state
        const { navigate } = this.props
        const page = this.getPage()

        /* Check Status */
        if (status !== "ok") {
            return (
                <div className="bkg--page">
                    <InnerLayout inner status={status} reload={() => this.load(true)} />
                </div>
            )
        }

        return (
            <div className="bkg--page">

                {/* Head */}
                <div className="bkg--table-header">
                    <div className="bkg--column bkg--col-number">#</div>
                    <div className="bkg--column bkg--col-game">{t("book.game")}</div>
                    <div className="bkg--column bkg--col-player">{t("book.player")}</div>
                    <div className="bkg--column bkg--col-window">{t("book.window")}</div>
                    <div className="bkg--column bkg--col-state">{t("book.status")}</div>
                    <div className="bkg--column bkg--col-author">{t("book.author")}</div>
                    <div className="bkg--column bkg--col-actions" />
                </div>

                {/* Empty — the two tabs are empty for two different reasons */}
                {data.length === 0 &&
                    <div className="bkg--empty">
                        <CalendarIcon size={40} />
                        <span>
                            {search !== "" || this.getCount() > 0
                                ? t("book.nothingFound")
                                : tab === "upcoming" ? t("book.emptyUpcoming") : t("book.emptyHistory")
                            }
                        </span>
                        {search === "" && this.getCount() === 0 && tab === "upcoming" &&
                            <p>{t("book.emptyUpcomingNote")}</p>
                        }
                    </div>
                }

                {/* Body */}
                {data.map((item, index) =>
                    <div className={`bkg--row ${getState(item)}`} key={`${index}`}>

                        {/* Number */}
                        <div className="bkg--column bkg--col-number">{(page - 1) * SIZE + index + 1}</div>

                        {/* Game */}
                        <div className="bkg--column bkg--col-game">

                            <div className={`bkg--icon ${getState(item)}`}>
                                <TableIcon size={18} />
                            </div>

                            <div className="bkg--game-text">
                                <div className="bkg--game-name">{getGame(item)}</div>
                                <div className="bkg--game-note">
                                    <StudioIcon size={11} />
                                    {utils.checkValue(item.studio, "—")}
                                </div>
                            </div>

                        </div>

                        {/* Player */}
                        <div className="bkg--column bkg--col-player">

                            <Avatar uri={getAvatar(item)} size={38} />

                            <div className="bkg--player-text">

                                <div className="bkg--player-name">
                                    <span className="bkg--player-value">{getPlayerName(item)}</span>
                                    {parseInt(item.isVip) === 1 && <span className="bkg--tag vip"><VipIcon size={10} /> VIP</span>}
                                    {parseInt(item.isTester) === 1 && <span className="bkg--tag tester">{t("book.tester")}</span>}
                                </div>

                                <div className="bkg--player-code">{item.player}</div>

                                <div className="bkg--operator" title={utils.checkValue(item.operator, "")}>
                                    <OperatorIcon size={11} />
                                    <span>{utils.checkValue(item.operator, "—")}</span>
                                </div>

                            </div>

                        </div>

                        {/* Window */}
                        <div className="bkg--column bkg--col-window">
                            <div className="bkg--when">{formatWhen(item.startAt)}</div>
                            <div className="bkg--range-value">{formatRange(item)}</div>
                            <div className="bkg--duration">{formatDuration(item)}</div>
                            <div className="bkg--countdown">{formatCountdown(item)}</div>
                        </div>

                        {/* Status */}
                        <div className="bkg--column bkg--col-state">

                            <div className={`bkg--state ${getState(item)}`}>{getStateText(item)}</div>

                            {item.comment
                                ? <div className="bkg--comment" title={item.comment}>
                                    <CommentIcon size={11} />
                                    <span>{item.comment}</span>
                                </div>
                                : null
                            }

                            {getState(item) === "cancelled" && item.cancelReason
                                ? <div className="bkg--reason" title={item.cancelReason}>{item.cancelReason}</div>
                                : null
                            }

                        </div>

                        {/* Author */}
                        <div className="bkg--column bkg--col-author">

                            <div className="bkg--author">
                                <AdminIcon size={12} />
                                <span>{utils.checkValue(item.admin, "—")}</span>
                            </div>

                            <div className="bkg--author-note">{formatDate(item.createdAt)}</div>

                        </div>

                        {/* Actions */}
                        <div className="bkg--column bkg--col-actions">{this._actions(item)}</div>

                    </div>
                )}

                {/* Pagination */}
                <Pagination size={SIZE} page={page} total={count} onPageChange={(page) => navigate(`/booking/${page}`)} />

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { status } = this.state

        return (
            <div className="page">

                <div className="page-header bkg--header">

                    <h1>{t("book.title")}</h1>

                    <div className="bkg--tools">

                        {/* Draw Search */}
                        {status === "ok" && this._search()}

                        {/* Draw Filter Button */}
                        {status === "ok" && this._filter()}

                        {/* Draw Create Button */}
                        {this._create()}

                    </div>

                </div>

                {/* Draw Tabs */}
                {this._tabs()}

                {/* Draw Content */}
                {this._content()}

                {/* Draw Create Modal */}
                <BookingForm reload={() => this.load(true)} ref={(ref) => this._formModal = ref} />

                {/* Draw Window Modal — продлить или сократить время уже созданной брони */}
                <BookingWindow reload={() => this.load(true)} ref={(ref) => this._windowModal = ref} />

                {/* Draw Filter Modal */}
                <BookingFilter apply={this.applyFilter} ref={(ref) => this._filterModal = ref} />

            </div>
        )
    }

}

const mapStateToProps = (state) => ({
    admin: state.admin,
    language: state.language,
    timezone: state.timezone,
})

export default connect(mapStateToProps)(withRouter(Bookings))
